import React, { useState, useMemo } from 'react';
import { Vessel, ImportRecord, BitumenGrade, User } from '../../types';
// Added comment above fix: Added MapPin to the lucide-react imports to resolve the 'Cannot find name MapPin' error on line 81
import { Ship as ShipIcon, Calendar as CalendarIcon, Trash2 as TrashIcon, ChevronDown as ChevronDownIcon, ChevronUp as ChevronUpIcon, Edit3, ShieldCheck, AlertTriangle, MapPin } from 'lucide-react';
import { ManifestEditModal } from './ManifestEditModal';

interface VesselListProps {
  vessels: Vessel[];
  onDelete: (id: string) => void;
  onUpdate: (vessel: Vessel) => void;
  currentUser: User;
}

/**
 * Normalizes bitumen grade strings to a unified 'XX/YY' format.
 * Strips 'Bitumen', 'Grade', and handles common typos.
 */
const formatGrade = (grade: string) => {
    if (!grade) return '-';
    let g = grade.toUpperCase()
        .replace(/BITUMEN/g, '')
        .replace(/GRADE/g, '')
        .replace(/FRADE/g, '')
        .trim();
    
    // Typo corrections for common grades
    if (g === '80/10') g = '80/100';
    if (g === '60/7') g = '60/70';
    
    return g || grade;
};

const ManifestDetailsTable: React.FC<{ manifest: ImportRecord[]; vesselName: string }> = ({ manifest, vesselName }) => {
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const sortedManifest = useMemo(() => {
        if (!sortConfig) return manifest;
        return [...manifest].sort((a, b) => {
             const key = sortConfig.key as keyof ImportRecord;
             let valA = a[key];
             let valB = b[key];
             if (key === 'quantity') return sortConfig.direction === 'asc' ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
             valA = (valA || '').toString().toLowerCase();
             valB = (valB || '').toString().toLowerCase();
             return sortConfig.direction === 'asc' ? (valA < valB ? -1 : 1) : (valA > valB ? -1 : 1);
        });
    }, [manifest, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig(prev => ({ key, direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-inner animate-in fade-in duration-300">
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        <tr>
                            <th className="px-6 py-4 text-left cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('importerName')}>Importer/Consignee</th>
                            <th className="px-6 py-4 text-left cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('notifyParty')}>Notify Party</th>
                            <th className="px-6 py-4 text-left cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('supplier')}>Supplier</th>
                            <th className="px-6 py-4 text-left cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('bitumenGrade')}>Grade</th>
                            <th className="px-6 py-4 text-right cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('quantity')}>Quantity</th>
                            <th className="px-6 py-4 text-left cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('finalDestination')}>Final Destination</th>
                            <th className="px-6 py-4 text-left cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => handleSort('blNumber')}>BL Number</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {sortedManifest.map(item => (
                            <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-6 py-4 font-bold text-slate-700">{item.importerName}</td>
                                <td className="px-6 py-4 text-slate-500 text-xs">{item.notifyParty || '-'}</td>
                                <td className="px-6 py-4 text-slate-500">{item.supplier}</td>
                                <td className="px-6 py-4">
                                    <span className="px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-600 text-[10px] font-black border border-indigo-100 uppercase tracking-tighter shadow-sm">
                                        {formatGrade(item.bitumenGrade)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-right font-black text-slate-900">{item.quantity.toLocaleString()} MT</td>
                                <td className="px-6 py-4">
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-tight flex items-center gap-1.5">
                                        <MapPin size={10} className="text-indigo-400" />
                                        {item.finalDestination || '-'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 font-mono text-xs text-indigo-600 font-black">{item.blNumber || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const VesselList: React.FC<VesselListProps> = ({ vessels, onDelete, onUpdate, currentUser }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingVessel, setEditingVessel] = useState<Vessel | null>(null);

  const isAdmin = currentUser.role.toLowerCase() === 'admin';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-2 px-2">
        <div>
           <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Vessel Registry</h2>
           <p className="text-slate-500 text-sm font-medium">Monitoring regional maritime logistics and bitumen consignments.</p>
        </div>
        {isAdmin && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 px-4 py-2 rounded-2xl">
                <ShieldCheck className="text-rose-600" size={16} />
                <span className="text-[10px] font-black text-rose-700 uppercase tracking-widest">Admin Delete Active</span>
            </div>
        )}
      </div>

      <div className="grid gap-6">
        {vessels.map(vessel => (
          <div key={vessel.id} className="bg-white rounded-[40px] shadow-sm border border-slate-200 overflow-hidden transition-all hover:shadow-xl hover:border-indigo-200 group">
            <div 
              className="p-6 flex items-center justify-between cursor-pointer"
              onClick={() => setExpandedId(expandedId === vessel.id ? null : vessel.id)}
            >
              <div className="flex items-center space-x-5">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${vessel.status === 'Discharging' ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white group-hover:rotate-3 shadow-sm'}`}>
                  <ShipIcon size={28} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{vessel.name}</h3>
                  <div className="flex items-center text-[10px] font-black text-slate-400 uppercase tracking-widest gap-4 mt-1.5">
                    <span className="flex items-center gap-1.5"><CalendarIcon size={12} className="text-slate-300" /> {vessel.arrivalDate}</span>
                    <span className={`px-3 py-1 rounded-full border ${vessel.status === 'Discharging' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {vessel.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-8">
                <div className="text-right hidden sm:block">
                  <div className="text-xl font-black text-slate-900 leading-none">
                    {vessel.manifest.reduce((sum, item) => sum + (item.quantity || 0), 0).toLocaleString()} <span className="text-xs text-slate-400 font-bold">MT</span>
                  </div>
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{vessel.manifest.length} B/L Records</div>
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={(e) => { e.stopPropagation(); setEditingVessel(vessel); }}
                        className="p-3 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                        title="Edit Registry Entry"
                    >
                        <Edit3 size={18} />
                    </button>
                    {isAdmin && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onDelete(vessel.id); }}
                            className="p-3 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                            title="Purge Vessel Record"
                        >
                            <TrashIcon size={18} />
                        </button>
                    )}
                    <div className={`p-3 rounded-xl transition-colors ${expandedId === vessel.id ? 'bg-indigo-50 text-indigo-600' : 'text-slate-300'}`}>
                        {expandedId === vessel.id ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                    </div>
                </div>
              </div>
            </div>

            {expandedId === vessel.id && (
              <div className="bg-slate-50/50 border-t border-slate-100 p-8 animate-in slide-in-from-top-2 duration-300">
                <div className="mb-6 flex items-center justify-between">
                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-[0.2em] flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                        Manifest Recon: {vessel.name}
                    </h4>
                </div>
                <ManifestDetailsTable manifest={vessel.manifest} vesselName={vessel.name} />
              </div>
            )}
          </div>
        ))}
        {vessels.length === 0 && (
            <div className="py-24 text-center bg-white rounded-[40px] border-2 border-dashed border-slate-100">
                <ShipIcon size={48} className="mx-auto text-slate-100 mb-4" />
                <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">No Vessels Registered</p>
            </div>
        )}
      </div>

      <ManifestEditModal 
        isOpen={!!editingVessel}
        onClose={() => setEditingVessel(null)}
        vessel={editingVessel}
        onSave={onUpdate}
      />
    </div>
  );
};

export default VesselList;